<template>
  <div class="email-auth-dashboard">
    <div class="dashboard-header">
      <h2>Email Authorization Tracking</h2>
    </div>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search client name or auth #"
            clearable
            @input="onSearch"
            @clear="onSearch"
            class="search-input"
          >
            <i slot="prepend" class="fa fa-search"></i>
          </el-input>
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="selectedFilter"
            :options="statusFilterOptions"
            placeholder="Status"
            clearable
            class="compact-filter"
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="officeFilter"
            :options="offices"
            placeholder="Office"
            clearable
            class="compact-filter-wide"
            @input="onFilterChange"
          />
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="adminFilter"
            :options="admins"
            placeholder="Admin"
            clearable
            class="compact-filter"
            @input="onFilterChange"
          />
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="paginationTotal > 0" class="pagination-wrapper">
      <div class="pagination-info">
        <span class="total-badge">{{ paginationTotal }} {{ paginationTotal === 1 ? 'item' : 'items' }}</span>
        <div class="page-size-selector">
          <span class="page-size-label">Show:</span>
          <AIVSelect
            :options="pageSizeOptions"
            :value="pagination.perPage"
            @input="onPageSizeChange"
            :searchable="false"
            :clearable="false"
            :custom-style="{
              width: '70px',
              height: '28px',
              fontSize: '12px',
              backgroundColor: '#656565',
              border: '1px solid #bdbdbd'
            }"
            placeholder=""
          />
        </div>
      </div>
      <div v-if="showNavigationControls" class="pagination-controls">
        <div class="pagination-nav">
          <button
            class="pagination-btn"
            @click="pageChange(1)"
            :disabled="pagination.currentPage === 1"
          >
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button
            class="pagination-btn"
            @click="pageChange(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
          >
            <i class="fas fa-angle-left"></i>
          </button>
          <span class="pagination-current">
            Page {{ pagination.currentPage }} of {{ pagination.totalPages }}
          </span>
          <button
            class="pagination-btn"
            @click="pageChange(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
          >
            <i class="fas fa-angle-right"></i>
          </button>
          <button
            class="pagination-btn"
            @click="pageChange(pagination.totalPages)"
            :disabled="pagination.currentPage === pagination.totalPages"
          >
            <i class="fas fa-angle-double-right"></i>
          </button>
          <div class="go-to-page">
            <span class="go-to-label">Go to:</span>
            <input
              type="number"
              v-model.number="goToPageInput"
              @keyup.enter="goToPage"
              :min="1"
              :max="pagination.totalPages"
              class="go-to-input"
              placeholder="Page"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Email Authorizations Table -->
    <div class="table-container">
      <table class="email-table">
        <thead>
          <tr>
            <th class="sortable-th" @click="toggleSort('clientName')">
              Client
              <i :class="getSortIcon('clientName')" class="sort-icon"></i>
            </th>
            <th>Auth #</th>
            <th>Service</th>
            <th class="sortable-th" @click="toggleSort('hoursRemaining')">
              Hours
              <i :class="getSortIcon('hoursRemaining')" class="sort-icon"></i>
            </th>
            <th>Sent To</th>
            <th>Sent By</th>
            <th>Sent Date</th>
            <th>Status</th>
            <th>Replies</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading && filteredNotifications.length === 0">
            <td colspan="10" class="loading-cell">
              <i class="fas fa-spinner fa-spin"></i> Loading...
            </td>
          </tr>
          <tr v-else-if="filteredNotifications.length === 0">
            <td colspan="10" class="empty-cell">
              No email authorizations found
            </td>
          </tr>
          <tr
            v-for="notification in filteredNotifications"
            :key="notification.id"
            :class="{
              'has-unread': notification.hasUnreadReplies,
              'pending-approval-row': notification.status === 'pending',
              'clickable': notification.status === 'pending'
            }"
            @click="notification.status === 'pending' ? handlePendingClick(notification) : null"
          >
            <td class="client-name">{{ notification.clientName }}</td>
            <td class="auth-number">{{ notification.authNumber }}</td>
            <td class="service-code">
              <span class="badge">{{ notification.serviceCode }}</span>
            </td>
            <td class="hours-remaining">
              <span
                class="hours-badge"
                :class="getHoursClass(notification.hoursRemaining)"
              >
                {{ notification.hoursRemaining }}h
              </span>
            </td>
            <td class="recipients">
              {{ notification.status === 'pending' ? '-' : formatRecipients(notification.recipients) }}
            </td>
            <td class="sent-by">
              {{ notification.status === 'pending' ? '-' : notification.sentBy }}
            </td>
            <td class="sent-date">
              {{ notification.status === 'pending' ? '-' : formatDate(notification.sentAt) }}
            </td>
            <td class="status">
              <span class="status-badge" :class="notification.status">
                {{ formatStatus(notification.status) }}
              </span>
            </td>
            <td class="reply-count">
              <span v-if="notification.replyCount > 0" class="reply-badge">
                <i class="fas fa-reply"></i>
                {{ notification.replyCount }}
              </span>
              <span v-else class="no-replies">-</span>
            </td>
            <td class="actions">
              <button
                v-if="notification.status === 'pending'"
                @click.stop="handlePendingClick(notification)"
                class="action-btn approve-btn"
                title="Review and approve/reject"
              >
                <i class="fas fa-eye"></i> Review
              </button>
              <template v-if="notification.status !== 'pending'">
                <button
                  @click="viewThread(notification)"
                  class="action-btn view-btn"
                  title="View email thread"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button
                  v-if="
                    notification.status === 'awaiting_reply' ||
                    notification.status === 'reply_received'
                  "
                  @click="checkReplies(notification)"
                  class="action-btn check-btn"
                  title="Check for new replies"
                >
                  <i class="fas fa-sync"></i>
                </button>
              </template>
              <button
                v-if="
                  notification.status === 'pending' ||
                  notification.status === 'sent' ||
                  notification.status === 'awaiting_reply' ||
                  notification.status === 'reply_received'
                "
                @click.stop="markAsCompleted(notification)"
                class="action-btn complete-btn"
                title="Mark as completed"
              >
                <i class="fas fa-check"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Email Thread Viewer Modal -->
    <EmailThreadViewer
      v-if="selectedNotification"
      :notification="selectedNotification"
      @close="selectedNotification = null"
      @reply-sent="handleReplySent"
    />
  </div>
</template>

<script>
import EmailThreadViewer from "./EmailThreadViewer.vue";
import { emailRepliesService } from "@/serviceHandlers/emailRepliesService";
import { officesService } from "@/serviceHandlers/officesService";
import { usersService } from "@/serviceHandlers/usersService";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "EmailAuthorizationDashboard",
  components: {
    EmailThreadViewer,
  },
  data() {
    return {
      notifications: [],
      loading: false,
      selectedNotification: null,
      selectedFilter: null,
      searchQuery: '',
      officeFilter: null,
      adminFilter: null,
      offices: [],
      admins: [],
      sortField: null,
      sortOrder: null,
      searchDebounceTimer: null,
      pagination: {
        total: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: 20,
      },
      pageSizeOptions: [
        { id: 20, name: '20' },
        { id: 50, name: '50' },
        { id: 100, name: '100' },
      ],
      goToPageInput: null,
    };
  },
  computed: {
    statusFilterOptions() {
      return [
        { id: 'pending', name: 'Pending Approval' },
        { id: 'awaiting_reply', name: 'Awaiting Reply' },
        { id: 'reply_received', name: 'Reply Received' },
        { id: 'completed', name: 'Completed' },
        { id: 'failed', name: 'Failed' },
        { id: 'rejected', name: 'Rejected' },
      ];
    },
    filteredNotifications() {
      return this.notifications;
    },
    paginationTotal() {
      return this.pagination.total;
    },
    showNavigationControls() {
      return this.pagination.total > 10;
    }
  },
  watch: {
    selectedFilter() {
      this.pagination.currentPage = 1;
      this.pagination.total = 0;
      this.pagination.totalPages = 0;
      this.notifications = [];
      this.loadDashboardData();
    },
  },

  async mounted() {
    this.loadDashboardData();
    try {
      const [officeList, userList] = await Promise.all([
        officesService.list(),
        usersService.list(),
      ]);
      this.offices = officeList;
      this.admins = (userList || [])
        .filter(u => u.user_type === 'Admin')
        .map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}` }));
    } catch (e) {
      // Non-critical: filters just won't populate
    }
    // Auto-refresh and check for replies every minute
    this.refreshInterval = setInterval(
      () => {
        this.loadDashboardData();
        this.checkAllReplies();
      },
      60 * 1000, // 1 minute
    );
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async loadDashboardData() {
      // Guard against being called when user is not an admin
      const loginStore = useLoginStore();
      if (!loginStore.isAdmin) return;

      this.loading = true;
      try {
        // Build query params for server-side pagination and filtering
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.perPage,
        };
        if (this.selectedFilter) {
          params.status = this.selectedFilter;
        }
        if (this.searchQuery) {
          params.search = this.searchQuery;
        }
        if (this.officeFilter) {
          params.officeId = this.officeFilter;
        }
        if (this.adminFilter) {
          params.adminId = this.adminFilter;
        }
        if (this.sortField) {
          params.sortField = this.sortField;
          params.sortOrder = this.sortOrder;
        }
        const response = await emailRepliesService.getDashboard(params);
        if (response.success) {
          this.notifications = response.notifications;
          if (response.pagination) {
            this.pagination.total = response.pagination.total;
            this.pagination.totalPages = response.pagination.totalPages;
          }
        }
      } catch (error) {
        // Silently fail on auto-refresh
      } finally {
        this.loading = false;
      }
    },
    pageChange(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination.currentPage = page;
      this.loadDashboardData();
    },
    onPageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.loadDashboardData();
    },
    goToPage() {
      if (this.goToPageInput >= 1 && this.goToPageInput <= this.pagination.totalPages) {
        this.pageChange(this.goToPageInput);
        this.goToPageInput = null;
      }
    },
    async checkAllReplies() {
      const loginStore = useLoginStore();
      if (!loginStore.isAdmin) return;

      // Check for new replies on all notifications that are awaiting reply or have received a reply
      const notificationsToCheck = this.notifications.filter(
        n => n.status === 'awaiting_reply' || n.status === 'reply_received'
      );

      for (const notification of notificationsToCheck) {
        try {
          await emailRepliesService.checkNotificationReplies(notification.id);
        } catch (error) {
          // Silently fail for individual checks
        }
      }

      // Reload data after checking all
      if (notificationsToCheck.length > 0) {
        await this.loadDashboardData();
      }
    },
    async checkReplies(notification) {
      try {
        const response = await emailRepliesService.checkNotificationReplies(notification.id);
        if (response.success) {
          if (response.newReplies > 0) {
            this.$notifySuccess(
              `Found ${response.newReplies} new reply(ies)`,
            );
            await this.loadDashboardData();
          } else {
            this.$notify("No new replies found", "info");
          }
        }
      } catch (error) {
        this.$notifyError("Failed to check for replies");
      }
    },
    async markAsCompleted(notification) {
      const confirmed = await this.$confirmAction(
        () => {},
        "Mark this email authorization as completed?",
      );
      if (!confirmed) return;
      try {
        const response = await emailRepliesService.markAsCompleted(notification.id);
        if (response.success) {
          this.$notifySuccess("Marked as completed");
          await this.loadDashboardData();
        }
      } catch (error) {
        this.$notifyError("Failed to mark as completed");
      }
    },
    viewThread(notification) {
      this.selectedNotification = notification;
    },
    async handleReplySent() {
      await this.loadDashboardData();
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
    formatStatus(status) {
      const statusMap = {
        pending: "Pending Approval",
        sent: "Sent",
        awaiting_reply: "Awaiting Reply",
        reply_received: "Reply Received",
        completed: "Completed",
        failed: "Failed",
        rejected: "Rejected",
      };
      return statusMap[status] || status;
    },
    handlePendingClick(notification) {
      this.$emit('open-auth-modal', notification);
    },
    formatRecipients(recipients) {
      if (!recipients) return "-";
      const emails = recipients.split(",").map((e) => e.trim());
      if (emails.length > 2) {
        return `${emails.slice(0, 2).join(", ")} +${emails.length - 2}`;
      }
      return recipients;
    },
    getHoursClass(hours) {
      if (hours <= 5) return "critical";
      if (hours <= 10) return "warning";
      return "good";
    },
    toggleSort(field) {
      if (this.sortField !== field) {
        this.sortField = field;
        this.sortOrder = 'asc';
      } else if (this.sortOrder === 'asc') {
        this.sortOrder = 'desc';
      } else {
        this.sortField = null;
        this.sortOrder = null;
      }
      this.pagination.currentPage = 1;
      this.loadDashboardData();
    },
    getSortIcon(field) {
      if (this.sortField !== field) return 'fas fa-sort';
      return this.sortOrder === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
    onSearch() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.currentPage = 1;
        this.loadDashboardData();
      }, 400);
    },
    onFilterChange() {
      this.pagination.currentPage = 1;
      this.loadDashboardData();
    },
  },
};
</script>

<style scoped>
.email-auth-dashboard {
  color: #e0e0e0;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dashboard-header h2 {
  color: #fff;
  margin: 0;
  font-size: 1.75rem;
}

/* Compact filter section (matching Authorizations page) */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.filter-group {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group.search-group {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.compact-filter {
  min-width: 225px;
  width: 225px;
}

.compact-filter-wide {
  min-width: 300px;
  width: 300px;
}

.sortable-th {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.sortable-th:hover {
  color: #17a2b8;
}

.sort-icon {
  margin-left: 0.3rem;
  font-size: 0.75rem;
  opacity: 0.7;
}

.table-container {
  background: rgba(37, 55, 65, 0.9);
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.email-table {
  width: 100%;
  border-collapse: collapse;
}

.email-table thead {
  background: rgba(255, 255, 255, 0.05);
}

.email-table th {
  padding: 1rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #fff;
  font-size: 0.9rem;
}

.email-table td {
  padding: 0.875rem 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #e0e0e0;
}

.email-table tbody tr {
  transition: background 0.2s;
}

.email-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.05);
}

.email-table tbody tr.has-unread {
  background: rgba(23, 162, 184, 0.05);
  border-left: 3px solid #17a2b8;
}

.email-table tbody tr.pending-approval-row {
  background: rgba(253, 126, 20, 0.05);
  border-left: 3px solid #fd7e14;
}

.email-table tbody tr.clickable {
  cursor: pointer;
}

.email-table tbody tr.clickable:hover {
  background: rgba(253, 126, 20, 0.1);
}

.badge {
  display: inline-block;
  padding: 0.25em 0.5em;
  border-radius: 0.25rem;
  background: rgba(108, 117, 125, 0.3);
  color: #b3a6a6;
  font-size: 0.85rem;
  font-weight: 600;
}

.hours-badge {
  display: inline-block;
  padding: 0.25em 0.5em;
  border-radius: 0.25rem;
  font-weight: 600;
  font-size: 0.85rem;
}

.hours-badge.critical {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.hours-badge.warning {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.hours-badge.good {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-badge {
  display: inline-block;
  padding: 0.25em 0.75em;
  border-radius: 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-badge.pending {
  background: rgba(253, 126, 20, 0.2);
  color: #fd7e14;
  font-weight: 700;
}

.status-badge.sent,
.status-badge.awaiting_reply {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-badge.reply_received {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
}

.status-badge.completed {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-badge.failed,
.status-badge.rejected {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.reply-badge {
  color: #17a2b8;
  font-weight: 600;
}

.no-replies {
  color: #b3a6a6;
}

.actions {
  display: flex;
  gap: 0.375rem;
}

.action-btn {
  padding: 0.375rem 0.625rem;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  background: rgba(108, 117, 125, 0.2);
  color: #b3a6a6;
  border: 1px solid rgba(108, 117, 125, 0.4);
  transition: all 0.2s;
}

.action-btn:hover {
  background: rgba(108, 117, 125, 0.3);
  border-color: rgba(108, 117, 125, 0.6);
}

.view-btn:hover {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
  border-color: rgba(23, 162, 184, 0.4);
}

.check-btn:hover {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
  border-color: rgba(255, 193, 7, 0.4);
}

.complete-btn:hover {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
  border-color: rgba(40, 167, 69, 0.4);
}

.approve-btn {
  background: rgba(253, 126, 20, 0.2);
  color: #fd7e14;
  border: 1px solid rgba(253, 126, 20, 0.4);
}

.approve-btn:hover {
  background: rgba(253, 126, 20, 0.3);
  color: #fd7e14;
  border-color: rgba(253, 126, 20, 0.6);
}

.loading-cell,
.empty-cell {
  text-align: center;
  padding: 2.5rem !important;
  color: #b3a6a6;
}

/* Pagination Styles (matching AIVTable) */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  margin-bottom: 6px;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-label {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.total-badge {
  display: inline-block;
  background-color: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
}

.pagination-controls {
  display: flex;
  align-items: center;
}

.pagination-nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  padding: 0;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  font-size: 10px;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #9ca3af;
}

.pagination-current {
  font-size: 12px;
  color: #ffffff;
  margin: 0 10px;
  font-weight: 500;
}

.go-to-page {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 16px;
}

.go-to-label {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.go-to-input {
  width: 50px;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #bdbdbd;
  background-color: #656565;
  color: #ffffff;
  font-size: 12px;
  border-radius: 3px;
  text-align: center;
  transition: all 0.15s ease-in-out;
  line-height: 26px;
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: center;
}

.go-to-input::-webkit-outer-spin-button,
.go-to-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.go-to-input[type=number] {
  -moz-appearance: textfield;
}

.go-to-input:focus {
  outline: none;
  border-color: #9ca3af;
  background-color: #656565;
}

.go-to-input::placeholder {
  color: #ffffff;
  font-size: 12px;
}

@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-group.search-group {
    max-width: unset;
  }

  .compact-filter,
  .compact-filter-wide {
    width: 100%;
    min-width: unset;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 12px;
  }
}
</style>
